import './App.css';
import GPUResults from './components/GPUResults';
import Filter from './components/Filter';
import { useEffect, useState } from 'react';
import {
  compareGigaVersions,
  compareNumericStrings,
  fetchGigahorseResults,
  sortResults
} from './csvData';

function App() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [cLevel, setCLevel] = useState("Any");
  const [kSize, setKSize] = useState("32");
  const [gigaVersion, setGigaVersion] = useState("Any");
  const [selectedGPU, setSelectedGPU] = useState("Any");

  useEffect(() => {
    let cancelled = false;

    const loadRows = async () => {
      setLoading(true);
      setError('');
      try {
        const nextRows = await fetchGigahorseResults();

        if (cancelled) {
          return;
        }

        setRows(nextRows);
      }
      catch (fetchError) {
        if (cancelled) {
          return;
        }

        console.error('Error fetching data: ', fetchError);
        setRows([]);
        setError('Unable to load the bundled Gigahorse CSV right now.');
      }
      finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadRows();

    return () => {
      cancelled = true;
    };
  }, []);

  const gpuArray = [...new Set(rows.map((row) => row.gpu_name))]
    .sort((left, right) => left.localeCompare(right, undefined, { numeric: true, sensitivity: 'base' }));
  const kSizeArray = [...new Set(rows.map((row) => row.k_size))]
    .sort(compareNumericStrings);
  const cLevelArray = [...new Set(rows.map((row) => row.c_level))]
    .sort(compareNumericStrings);
  const gigaVersionArray = [...new Set(rows.map((row) => row.giga_version))]
    .sort(compareGigaVersions);

  useEffect(() => {
    if (rows.length === 0) {
      return;
    }

    const nextGpuArray = [...new Set(rows.map((row) => row.gpu_name))];
    const nextKSizeArray = [...new Set(rows.map((row) => row.k_size))].sort(compareNumericStrings);
    const nextCLevelArray = [...new Set(rows.map((row) => row.c_level))].sort(compareNumericStrings);
    const nextGigaVersionArray = [...new Set(rows.map((row) => row.giga_version))].sort(compareGigaVersions);

    setSelectedGPU((current) => (current === 'Any' || nextGpuArray.includes(current) ? current : 'Any'));
    setCLevel((current) => (current === 'Any' || nextCLevelArray.includes(current) ? current : 'Any'));
    setGigaVersion((current) => (current === 'Any' || nextGigaVersionArray.includes(current) ? current : 'Any'));
    setKSize((current) => {
      if (nextKSizeArray.length === 0) {
        return current;
      }

      if (nextKSizeArray.includes(current)) {
        return current;
      }

      return nextKSizeArray.includes('32') ? '32' : nextKSizeArray[0];
    });
  }, [rows]);

  const filteredResults = sortResults(rows.filter((row) => {
    const matchesGpu = selectedGPU === 'Any' || row.gpu_name === selectedGPU;
    const matchesCLevel = cLevel === 'Any' || row.c_level === cLevel;
    const matchesKSize = row.k_size === kSize;
    const matchesGigaVersion = gigaVersion === 'Any' || row.giga_version === gigaVersion;

    return matchesGpu && matchesCLevel && matchesKSize && matchesGigaVersion;
  }));

  return (
    <div className="App">
      <header className="AppHeader">
        <p className="Eyebrow">Gigahorse Max Farm Viewer</p>
        <div className="AppHeaderRow">
          <div>
            <h1>Live spreadsheet results, no database.</h1>
            <p className="AppSummary">
              The app uses the CSV bundled with the current build and filters the results entirely in the browser.
            </p>
          </div>
        </div>
        <div className="AppMeta">
          <span>{loading ? 'Loading bundled results...' : `Loaded ${rows.length} results`}</span>
          <span>CSV updates ship with app builds</span>
        </div>
        {error ? <p className="AppError">{error}</p> : null}
      </header>
      <div className='AppContainer'>
        <Filter
          gpuArray={gpuArray}
          loading={loading}
          selectedGPU={selectedGPU}
          setSelectedGPU={setSelectedGPU}
        />

        <GPUResults
        selectedGPU={selectedGPU}
        cLevel={cLevel}
        setCLevel={setCLevel}
        kSize={kSize}
        setKSize={setKSize}
        gigaVersion={gigaVersion}
        setGigaVersion={setGigaVersion}
        gigaVersionArray={gigaVersionArray}
        cLevelArray={cLevelArray}
        kSizeArray={kSizeArray}
        gpuResults={filteredResults}
        loading={loading}
        />
      </div>
      <div className='Footer'>
        <div className='InternalFooterDiv'>
          <p>{loading ? 'Loading data...' : `Showing ${filteredResults.length} matching results`}</p>
          <p onClick={()=>{window.open("https://github.com/pht1207")}}>Made by Parker Throneberry</p>
        </div>
      </div>
    </div>
  );
}

export default App;
